<template>
  <div class="page-pair" :class="{ 'page-pair--scanning': scanning }">
    <template v-if="scanning">
      <scanner @scanned="onScanned" ref="scanner"></scanner>
      <p class="pair-scan-hint">Richt de camera op de QR-code</p>
      <button class="btn btn-outline-secondary pair-scan-cancel" @click="stopScanning">Code typen</button>
    </template>

    <template v-else>
      <img class="logo" src="/img/logo-negative.png">
      <div class="pair-card">
        <h1>Kiosk koppelen</h1>
        <p>Maak in Prixo een kiosk aan via <strong>Instellingen → Badge</strong>, en scan de QR-code of typ de code.</p>

        <form class="pair-form" @submit.prevent="submit(code)">
          <input v-model="code" class="pair-code" maxlength="8" placeholder="ABCD2345"
                 autocomplete="off" autocapitalize="characters" spellcheck="false">
          <button type="submit" class="btn btn-dark" :disabled="code.length !== 8 || busy">
            {{ busy ? 'Bezig met koppelen…' : 'Koppelen' }}
          </button>
          <div class="pair-divider"><span>of</span></div>
          <button type="button" class="btn btn-outline-secondary" @click="scanning = true">QR-code scannen</button>
        </form>

        <div v-if="error" class="alert alert-danger pair-error">{{ error }}</div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @import '../assets/scss/variables';

  .page-pair {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 16px;
    background: $primary;
    background-image: linear-gradient(30deg, hsla(0, 0%, 100%, .6), rgba(0, 0, 0, .1));

    .logo {
      width: 300px;
      max-width: 90%;
      margin-bottom: 2.5rem;
    }

    // The camera is a fixed video at z-index -100: a background on this page
    // would hide it, and the scan overlay would cover the controls.
    &.page-pair--scanning {
      background: transparent;
      justify-content: flex-end;
      padding-bottom: 6vh;
    }
  }

  .pair-card {
    width: 360px;
    max-width: 100%;
    padding: 2rem 1.75rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, .85);
    box-shadow: 0 10px 30px rgba(0, 0, 0, .12);

    h1 {
      margin: 0 0 .75rem;
      font-size: 1.5rem;
      font-weight: 700;
      color: $body-color;
    }

    p {
      margin: 0 0 1.5rem;
      font-size: $font-size-sm;
      line-height: 1.5;
      color: $secondary;
    }
  }

  .pair-form {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .btn { width: 100%; font-size: $font-size-default; }
  }

  .pair-code {
    width: 100%;
    padding: 14px;
    border: 2px solid rgba(0, 0, 0, .12);
    border-radius: 8px;
    background: #fff;
    color: $body-color;
    font-family: Menlo, Consolas, monospace;
    font-size: 1.6rem;
    letter-spacing: .3em;
    text-align: center;
    text-transform: uppercase;
    transition: border-color .15s ease-out;

    &::placeholder { color: #c4c4c4; }
    &:focus { outline: none; border-color: $primary; }
  }

  .btn-dark {
    background: $body-color;
    color: #fff;
    font-weight: bold;

    &:hover:not(:disabled) { background: #000; }
    &:disabled { opacity: .35; cursor: not-allowed; }
  }

  .pair-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    color: $secondary;
    font-size: $font-size-sm;

    &:before, &:after {
      content: '';
      flex: 1;
      height: 1px;
      background: rgba(0, 0, 0, .12);
    }
  }

  .pair-error {
    margin-top: 1rem;
    border-radius: 8px;
    font-size: $font-size-sm;
  }

  .pair-scan-hint,
  .pair-scan-cancel {
    position: relative;
    z-index: 1;
  }

  .pair-scan-hint {
    margin: 0 0 1rem;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 1px 4px rgba(0, 0, 0, .6);
  }

  .pair-scan-cancel {
    min-width: 220px;
    background: rgba(255, 255, 255, .9);

    &:hover, &:focus { background: $secondary; }
  }
</style>

<script type="text/javascript">
  import Scanner from './../components/Scanner.vue'
  import kiosk from './../classes/kiosk'

  export default {
    components: { Scanner },
    data: function () {
      return { code: '', scanning: false, busy: false, error: null };
    },
    methods: {
      stopScanning: function () {
        // components/Scanner.vue has no beforeDestroy: stop the camera explicitly.
        this.$refs.scanner && this.$refs.scanner.stopStream();
        this.scanning = false;
      },
      onScanned: function (data) {
        if (data && data.pair) {
          this.stopScanning();
          this.submit(data.pair);
        }
      },
      submit: function (code) {
        this.busy = true;
        this.error = null;
        kiosk.pair(code)
          .then(() => this.$router.replace({ name: 'home' }))
          .catch(err => { this.error = err.message; })
          .finally(() => { this.busy = false; });
      },
    },
  };
</script>
